import { useRateManagement } from '@api/useFreight/useFreight'
import type { Surcharge } from '@api/useFreight/types'
import { useQueryClient } from '@tanstack/react-query'
import { DatePicker, InputNumber, message, Popconfirm, Popover, Switch, theme, Tooltip } from 'antd'
import dayjs from 'dayjs'
import * as React from 'react'
import { GraviButton, Horizontal, Texto, Vertical } from '@gravitate-js/excalibrr'

/**
 * Renders a switch for the active status of a surcharge with popconfirm
 * This follows AG-Grid's cell renderer pattern
 */
export function ActiveRenderer({
  value,
  data,
}: {
  value: boolean
  data: Surcharge
}) {
  const { token } = theme.useToken()
  const { useDisableSurcharge, useCreateSurchargeRate } = useRateManagement()
  const disableSurchargeMutation = useDisableSurcharge()
  const createSurchargeRateMutation = useCreateSurchargeRate()
  const queryClient = useQueryClient()

  const [activationDate, setActivationDate] = React.useState<dayjs.Dayjs | null>(null)
  const [rate, setRate] = React.useState<number | null>(null)
  const [popoverVisible, setPopoverVisible] = React.useState(false)
  const [popconfirmVisible, setPopconfirmVisible] = React.useState(false)

  // If data is undefined, show nothing
  if (!data || !data._id) {
    return null
  }

  const isPercentageType = data.type === 'Percentage'

  // Get the last non-zero rate
  const getLastActiveRate = React.useCallback(() => {
    if (!data.rates || data.rates.length === 0) return null

    // Sort rates by effective_from date in descending order
    const sortedRates = [...data.rates].sort(
      (a, b) => new Date(b.effective_from).getTime() - new Date(a.effective_from).getTime()
    )

    // Find the last rate that wasn't zero
    const lastActiveRate = sortedRates.find((rate) => rate.surcharge !== 0)
    return lastActiveRate || null
  }, [data.rates])

  // Initialize rate state with the last active rate
  React.useEffect(() => {
    const lastActiveRate = getLastActiveRate()
    if (lastActiveRate) {
      // For percentage types, convert decimal to percentage for display (e.g., 0.15 -> 15%)
      const displayRate = isPercentageType ? lastActiveRate.surcharge * 100 : lastActiveRate.surcharge
      setRate(displayRate)
    }
  }, [isPercentageType, getLastActiveRate])

  const handleRateChange = (value: number | null) => {
    setRate(value)
  }

  const handleActivationChange = (checked: boolean) => {
    if (checked && value) {
      // When toggling from disabled to enabled, show the date picker popover
      setPopoverVisible(true)
      // Set the activation date to today
      setActivationDate(dayjs().startOf('day'))
      // Set the initial rate from the last active rate
      const lastActiveRate = getLastActiveRate()
      if (lastActiveRate) {
        const displayRate = isPercentageType ? lastActiveRate.surcharge * 100 : lastActiveRate.surcharge
        setRate(displayRate)
      }
    } else if (!checked && !value) {
      // When toggling from enabled to disabled, show the popconfirm
      setPopconfirmVisible(true)
    }
  }

  const handleDateConfirm = async () => {
    if (!activationDate || rate === null) return

    // Convert percentage back to decimal for API
    const apiRate = isPercentageType ? rate / 100 : rate

    try {
      // Create a new rate with the selected activation date using the rate/create mutation
      // The createSurchargeRateMutation already uses the correct endpoint: 'freight/surcharge/rate/create'
      // The backend will handle both creating the rate AND enabling the surcharge
      await createSurchargeRateMutation.mutateAsync({
        surcharge_id: data._id,
        effective_date: activationDate.toISOString(),
        surcharge: apiRate,
      })

      // Update the UI
      await queryClient.invalidateQueries({ queryKey: ['surcharges'] })
      setPopoverVisible(false)
      setActivationDate(null)
      message.success('Surcharge activated successfully')
    } catch (error) {
      console.error('Error activating surcharge:', error)
      message.error('Failed to activate surcharge')
      setPopoverVisible(false)
    }
  }

  const handleDisable = async () => {
    // Call the API to disable the surcharge
    try {
      await disableSurchargeMutation.mutateAsync({
        surcharge_id: data._id,
        effective_from: dayjs().startOf('day').toISOString(),
      })
      setPopconfirmVisible(false)
      message.success('Surcharge deactivated successfully')
      queryClient.invalidateQueries({ queryKey: ['surcharges'] })
    } catch (error) {
      message.error('Failed to deactivate surcharge')
      console.error('Error disabling surcharge:', error)
    }
  }

  const activationContent = (
    <Vertical gap={16}>
      <Horizontal gap={16}>
        <Vertical>
          <Texto>Select activation date:</Texto>
          <DatePicker value={activationDate} onChange={setActivationDate} style={{ width: '100%' }} />
        </Vertical>
        <Vertical>
          <Texto>Rate:</Texto>
          <InputNumber
            value={rate}
            onChange={handleRateChange}
            placeholder='Enter rate'
            precision={isPercentageType ? 3 : 5}
            min={0}
            max={isPercentageType ? 200 : undefined}
            prefix={isPercentageType ? undefined : '$'}
            suffix={isPercentageType ? '%' : undefined}
            style={{ width: '100%' }}
            stringMode
            formatter={(value) => value}
            parser={(value) => value?.replace(/\$\s?|(,*)/g, '') || ''}
          />
        </Vertical>
      </Horizontal>
      <Horizontal justifyContent='flex-end' gap={8}>
        <GraviButton appearance='text' onClick={() => setPopoverVisible(false)}>
          Cancel
        </GraviButton>
        <GraviButton onClick={handleDateConfirm} disabled={!activationDate || rate === null}>
          Confirm
        </GraviButton>
      </Horizontal>
    </Vertical>
  )

  // Render the Switch with either Popover or Popconfirm based on the current state
  if (value) {
    // Surcharge is disabled, show Popover for enabling
    return (
      <Popover
        content={activationContent}
        title='Activate Surcharge'
        trigger='click'
        open={popoverVisible}
        onOpenChange={setPopoverVisible}
      >
        <Tooltip title='This surcharge will not generate any new line items'>
          <Switch
            checked={!value}
            onChange={(checked) => handleActivationChange(checked)}
            style={{ backgroundColor: !value ? token.colorSuccess : '' }}
            checkedChildren='Active'
            unCheckedChildren='Inactive'
          />
        </Tooltip>
      </Popover>
    )
  }

  // Surcharge is enabled, show Popconfirm for disabling
  return (
    <Popconfirm
      title='Deactivate Surcharge'
      description='This will disable the surcharge. Continue?'
      onConfirm={handleDisable}
      okText='Yes'
      cancelText='No'
      open={popconfirmVisible}
      onCancel={() => setPopconfirmVisible(false)}
    >
      <Tooltip title='This surcharge is actively generating line items'>
        <Switch
          checked={!value}
          onChange={(checked) => handleActivationChange(checked)}
          style={{ backgroundColor: !value ? token.colorSuccess : '' }}
          checkedChildren='Active'
          unCheckedChildren='Inactive'
        />
      </Tooltip>
    </Popconfirm>
  )
}
